import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase/config.js";

// Pedidos del menú público, numerados por negocio:
// - pedidos/{usuarioEmpresa}: contador con el número del último pedido (`ultimo`).
// - pedidos/{usuarioEmpresa}/lista/{numero}: cada pedido, con el número como id.
// Cualquier visitante crea el pedido y suma 1 al contador en la misma transacción
// (firestore.rules controla que los dos vayan juntos); solo el dueño los lee.

const counterRef = (usuarioEmpresa) => doc(db, "pedidos", usuarioEmpresa);
const listRef = (usuarioEmpresa) => collection(db, "pedidos", usuarioEmpresa, "lista");

export const orderNumber = (numero) => "#" + String(numero).padStart(6, "0");

// Igual que los límites de firestore.rules.
const MAX_LINES = 50;
const clip = (text, max) => String(text || "").trim().slice(0, max);

// `envio`: lo que el carrito sumó por el envío a domicilio (null si no corresponde o es a confirmar).
// `pago`: "mp" si el cliente eligió pagar con Mercado Pago. El estado del pago (`pagoMp`)
// lo escribe solo n8n (n8n/miqrapp-cobros-mp.js) con lo que dice Mercado Pago.
export const saveOrder = (business, cart, details, envio = null, pago = "") => {
    const items = cart.lines.slice(0, MAX_LINES).map((l) => ({
        id: l.product.id,
        nombre: clip(l.product.name, 120),
        precio: Number.isFinite(Number(l.product.price)) ? Number(l.product.price) : null,
        cantidad: l.qty,
        subtotal: Number.isFinite(l.subtotal) ? l.subtotal : null
    }));
    const entrega = details.entrega === "retiro" || details.entrega === "envio" ? details.entrega : "";

    const attempt = () => runTransaction(db, async (tx) => {
        const counter = await tx.get(counterRef(business.usuarioEmpresa));
        const numero = (Number(counter.data()?.ultimo) || 0) + 1;
        tx.set(counterRef(business.usuarioEmpresa), { ultimo: numero });
        tx.set(doc(listRef(business.usuarioEmpresa), String(numero)), {
            numero,
            fecha: serverTimestamp(),
            items,
            total: cart.total,
            envio: entrega === "envio" && Number.isFinite(envio) ? envio : null,
            entrega,
            nombre: clip(details.nombre, 60),
            direccion: entrega === "envio" ? clip(details.direccion, 120) : "",
            nota: clip(details.nota, 300),
            ...(pago === "mp" ? { pago: "mp" } : {})
        });
        return numero;
    });

    // Si dos clientes piden a la vez, las reglas rechazan al que llegó segundo (su número
    // ya está usado) en vez de dejar que la transacción reintente sola: se reintenta acá.
    const run = async (left) => {
        try {
            return await attempt();
        } catch (error) {
            if (left <= 0 || error.code !== "permission-denied") throw error;
            await new Promise((resolve) => setTimeout(resolve, 150 + Math.random() * 350));
            return run(left - 1);
        }
    }
    return run(4);
}

const fromDoc = (snap) => {
    const data = snap.data();
    return { ...data, fecha: data.fecha?.toDate ? data.fecha.toDate() : null };
}

// Los últimos `count` pedidos, del más nuevo al más viejo, y se actualiza solo cuando llega uno.
export const watchOrders = (usuarioEmpresa, count, onData, onError) =>
    onSnapshot(query(listRef(usuarioEmpresa), orderBy("numero", "desc"), limit(count)),
        (snap) => onData(snap.docs.map(fromDoc), snap.size === count),
        onError);

export const loadOrder = async (usuarioEmpresa, numero) => {
    const snap = await getDoc(doc(listRef(usuarioEmpresa), String(numero)));
    return snap.exists() ? fromDoc(snap) : null;
}
